import React, { createContext, useContext, useMemo } from "react";
import { HolderOutlined, SettingOutlined } from "@ant-design/icons";
import type { DragEndEvent, DraggableAttributes } from "@dnd-kit/core";
import { DndContext } from "@dnd-kit/core";
import type { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button, Checkbox, List, Popover, Space } from "antd";
import type { GetProps } from "antd";

export interface ColumnManagerItem {
  key: string;
  title: string;
  visible: boolean;
}

interface ColumnManagerProps {
  columns: ColumnManagerItem[];
  onToggle: (key: string, visible: boolean) => void;
  onReorder: (sourceKey: string, targetKey: string) => void;
  onShowAll: () => void;
  onHideAll: () => void;
  onResetDefaults: () => void;
}

interface SortableListItemContextProps {
  setActivatorNodeRef?: (element: HTMLElement | null) => void;
  listeners?: SyntheticListenerMap;
  attributes?: DraggableAttributes;
}

const SortableListItemContext = createContext<SortableListItemContextProps>({});

const DragHandle: React.FC = () => {
  const { setActivatorNodeRef, listeners, attributes } = useContext(
    SortableListItemContext
  );

  return (
    <Button
      type="text"
      size="small"
      icon={<HolderOutlined />}
      style={{ cursor: "move" }}
      ref={setActivatorNodeRef}
      {...attributes}
      {...listeners}
    />
  );
};

const SortableListItem: React.FC<GetProps<typeof List.Item> & { itemKey: string }> = (props) => {
  const { itemKey, style, ...rest } = props;

  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: itemKey });

  const listStyle: React.CSSProperties = {
    ...style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? { position: "relative", zIndex: 9999 } : {}),
  };

  const memoizedContextValue = useMemo<SortableListItemContextProps>(
    () => ({ setActivatorNodeRef, listeners, attributes }),
    [setActivatorNodeRef, listeners, attributes]
  );

  return (
    <SortableListItemContext.Provider value={memoizedContextValue}>
      <List.Item {...rest} ref={setNodeRef} style={listStyle} />
    </SortableListItemContext.Provider>
  );
};

export const ColumnManager: React.FC<ColumnManagerProps> = ({
  columns,
  onToggle,
  onReorder,
  onShowAll,
  onHideAll,
  onResetDefaults,
}) => {
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!active || !over) {
      return;
    }
    if (active.id !== over.id) {
      onReorder(String(active.id), String(over.id));
    }
  };

  const content = (
    <Space direction="vertical" size="small" style={{ minWidth: 320 }}>
      <Space>
        <Button size="small" onClick={onShowAll}>
          Show All
        </Button>
        <Button size="small" onClick={onHideAll}>
          Hide All
        </Button>
        <Button size="small" onClick={onResetDefaults}>
          Reset Defaults
        </Button>
      </Space>
      <DndContext
        modifiers={[restrictToVerticalAxis]}
        onDragEnd={onDragEnd}
        id="column-manager-drag-sorting"
      >
        <SortableContext
          items={columns.map((column) => column.key)}
          strategy={verticalListSortingStrategy}
        >
          <List
            dataSource={columns}
            size="small"
            bordered
            renderItem={(column) => (
              <SortableListItem key={column.key} itemKey={column.key}>
                <Space size="small">
                  <DragHandle />
                  <Checkbox
                    checked={column.visible}
                    onChange={(event) => onToggle(column.key, event.target.checked)}
                  >
                    {column.title}
                  </Checkbox>
                </Space>
              </SortableListItem>
            )}
          />
        </SortableContext>
      </DndContext>
    </Space>
  );

  return (
    <Popover content={content} trigger="click" placement="bottomRight">
      <Button icon={<SettingOutlined />}>Columns</Button>
    </Popover>
  );
};
